import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRenewalCards,
  getRenewedContract,
  getRenewalContract,
  getRenewalQuote,
  RENEWAL_DEFAULT_ORG_ID,
  renewContract,
  sendRenewalCode,
  sendRenewalPhotos,
  updateRenewalAddress,
  updateRenewalCustomer,
  updateRenewalQuoteProducts,
  validateRenewalCode,
  type RenewalCard,
  type RenewalContract,
  type RenewalQuote,
  type RenewedContractUploadContext
} from '~/services/modules/renewal';
import { getContractUploadFields } from '~/pages/contract/newJourney/getContractUploadFields';
import { validateContractFiles } from '~/pages/contract/newJourney/validateContractFiles';
import type { TypeFile } from '~/pages/contract/step4/hooks/types';
import type { IFile } from '~/services/modules/contract/types';
import { NEW_CARD_OPTION, type JourneyAddedCard, type JourneySavedCard } from '~/components/JourneyPayment/types';
import { usePaymentGatewayPublicKey } from '~/hooks/usePaymentGatewayPublicKey';
import { CardTokenizationError, tokenizeCardWithPagarme } from '~/services/modules/paymentGateway';
import { storeState } from '~/store';
import { toCardBrand } from '~/utils/creditCard';
import { reportError } from '~/utils/reportError';
import { getDefaultRenewalChannel, getRenewalChannelOptions, type RenewalChannel } from './renewalChannel';
import { isRenewalHttpError } from './renewalErrors';
import { buildRenewalPayload, getRenewalOutcome, type RenewalOutcome } from './renewalPayment';
import { buildRenewalReviewData, type RenewalAddressFields, type RenewalPersonalFields } from './renewalReview';
import {
  buildRenewalFinancials,
  buildRenewalSummaryRows,
  getRenewalCoverageItems,
  getRenewalInitialSelection,
  getRenewalUpsellItems
} from './renewalCoverage';
import type { FinancialData } from '../interfaces/financial-data';
import { getPreviousRenewalStep, type RenewalCodeStatus, type RenewalStep } from './renewalSteps';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

type UseRenewalJourneyParams = {
  contractHash: string;
  organizationId?: string | null;
};

/** Tempo em que «Código verificado.» fica na tela antes do avanço automático para a revisão. */
export const RENEWAL_VERIFIED_ADVANCE_MS = 800;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Estado e efeitos da renovação 2026. A página só renderiza o que este hook expõe.
 * Antes da verificação, busca apenas o contrato (resumo e contatos mascarados); cotação e cartões
 * só depois do código validado. A verificação vive só em memória: recarregar recomeça.
 */
export function useRenewalJourney({ contractHash, organizationId }: UseRenewalJourneyParams) {
  const orgId = organizationId || RENEWAL_DEFAULT_ORG_ID;
  const [step, setStep] = useState<RenewalStep>('identify');
  const [loadState, setLoadState] = useState<Exclude<LoadState, 'idle'>>('loading');
  const [contract, setContract] = useState<RenewalContract | null>(null);
  const [channel, setChannel] = useState<RenewalChannel>('sms');
  const [sendingCode, setSendingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [code, setCodeState] = useState('');
  const [codeStatus, setCodeStatus] = useState<RenewalCodeStatus>('idle');
  const [quote, setQuote] = useState<RenewalQuote | null>(null);
  const [cards, setCards] = useState<RenewalCard[]>([]);
  const [quoteState, setQuoteState] = useState<LoadState>('idle');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [financials, setFinancials] = useState<FinancialData | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [paymentSelection, setPaymentSelection] = useState<string | null>(null);
  const [addedCards, setAddedCards] = useState<JourneyAddedCard[]>([]);
  const [paying, setPaying] = useState(false);
  const [outcome, setOutcome] = useState<Exclude<RenewalOutcome, 'outdated'> | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const payingRef = useRef(false);
  const [renewedContractId, setRenewedContractId] = useState<string | null>(null);
  const [photosContext, setPhotosContext] = useState<RenewedContractUploadContext | null>(null);
  const [photosLoadState, setPhotosLoadState] = useState<LoadState>('idle');
  const [photoFiles, setPhotoFiles] = useState<Partial<Record<TypeFile, IFile>>>({});
  const [photoErrors, setPhotoErrors] = useState<Partial<Record<TypeFile, boolean>>>({});
  const [sendingPhotos, setSendingPhotos] = useState(false);
  const sendingPhotosRef = useRef(false);
  const { publicKey, loading: publicKeyLoading } = usePaymentGatewayPublicKey(orgId);
  const busyRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoadState('loading');
    getRenewalContract(contractHash, orgId)
      .then((data) => {
        if (!active) return;
        setContract(data);
        setChannel(getDefaultRenewalChannel(data.insured ?? {}));
        setLoadState('ready');
      })
      .catch((error: unknown) => {
        if (!active) return;
        reportError('Erro ao carregar contrato da renovação 2026', error, {
          context: 'renewal_load_contract',
          flow: 'renewal',
          contractHash
        });
        setLoadState('error');
      });
    return () => {
      active = false;
    };
  }, [contractHash, orgId]);

  /** O outro canal, quando o segurado tem esse contato. */
  const alternateChannel = useMemo<RenewalChannel | null>(() => {
    if (!contract) return null;
    const other = getRenewalChannelOptions(contract.insured ?? {}).find((option) => option.channel !== channel);
    return other && !other.disabled ? other.channel : null;
  }, [channel, contract]);

  const resetCode = useCallback(() => {
    setCodeState('');
    setCodeStatus('idle');
  }, []);

  const goBack = useCallback(() => {
    setStep((current) => getPreviousRenewalStep(current) ?? current);
    resetCode();
  }, [resetCode]);

  const changeContact = useCallback(() => {
    resetCode();
    setStep('identify');
  }, [resetCode]);

  /** Editar o código limpa o «Código incorreto.». */
  const setCode = useCallback((next: string) => {
    setCodeState(next);
    setCodeStatus((current) => (current === 'error' ? 'idle' : current));
  }, []);

  /** Envia (ou reenvia) o código; o ref impede envio duplo antes do re-render. */
  const deliverCode = useCallback(
    async (target: RenewalChannel, mode: 'send' | 'resend') => {
      if (!contract || busyRef.current) return false;
      busyRef.current = true;
      const setBusy = mode === 'send' ? setSendingCode : setResendingCode;
      setBusy(true);
      try {
        await sendRenewalCode(contract.insured.document, target);
        return true;
      } catch (error: unknown) {
        reportError('Erro ao enviar código da renovação 2026', error, {
          context: 'renewal_send_code',
          flow: 'renewal',
          contractHash,
          channel: target,
          mode
        });
        storeState.addToast({
          title: 'Não foi possível enviar o código',
          message: 'Verifique sua conexão e tente novamente.',
          type: 'error'
        });
        return false;
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [contract, contractHash]
  );

  const sendCode = useCallback(async () => {
    if (await deliverCode(channel, 'send')) {
      resetCode();
      setStep('verify');
    }
  }, [channel, deliverCode, resetCode]);

  const resendCode = useCallback(async () => {
    if (await deliverCode(channel, 'resend')) {
      resetCode();
      storeState.addToast({ title: 'Código reenviado', message: 'Enviamos um novo código.', type: 'success' });
    }
  }, [channel, deliverCode, resetCode]);

  const switchChannel = useCallback(async () => {
    if (!alternateChannel) return;
    if (await deliverCode(alternateChannel, 'resend')) {
      setChannel(alternateChannel);
      resetCode();
    }
  }, [alternateChannel, deliverCode, resetCode]);

  /** Cotação e cartões salvos; só depois do código validado. */
  const loadQuoteAndCards = useCallback(async () => {
    setQuoteState('loading');
    try {
      const loadedQuote = await getRenewalQuote(contractHash, orgId);
      setQuote(loadedQuote);
      setSelectedIds(getRenewalInitialSelection(loadedQuote));
      setFinancials(buildRenewalFinancials(loadedQuote));
      const customerId = loadedQuote?.insured?.id;
      const savedCards = customerId ? await getRenewalCards(customerId, orgId) : [];
      setCards(savedCards);
      setPaymentSelection(savedCards[0]?.id ?? NEW_CARD_OPTION);
      setQuoteState('ready');
    } catch (error: unknown) {
      reportError('Erro ao carregar cotação/cartões da renovação 2026', error, {
        context: 'renewal_load_quote',
        flow: 'renewal',
        contractHash
      });
      setQuoteState('error');
    }
  }, [contractHash, orgId]);

  const verifyCode = useCallback(async () => {
    if (!contract || busyRef.current) return;
    busyRef.current = true;
    setCodeStatus('verifying');
    try {
      await validateRenewalCode(contract.insured.document, code, channel);
      setCodeStatus('success');
      await Promise.all([loadQuoteAndCards(), wait(RENEWAL_VERIFIED_ADVANCE_MS)]);
      setStep('review');
    } catch (error: unknown) {
      if (isRenewalHttpError(error)) {
        setCodeStatus('error');
      } else {
        reportError('Erro de rede ao validar código da renovação 2026', error, {
          context: 'renewal_validate_code',
          flow: 'renewal',
          contractHash
        });
        storeState.addToast({
          title: 'Não foi possível verificar o código',
          message: 'Verifique sua conexão e tente novamente.',
          type: 'error'
        });
        setCodeStatus('idle');
      }
    } finally {
      busyRef.current = false;
    }
  }, [channel, code, contract, contractHash, loadQuoteAndCards]);

  const reviewData = useMemo(() => (contract ? buildRenewalReviewData(contract, quote) : null), [contract, quote]);

  /** `PUT Quotes/{quoteId}/customer`; CPF e nascimento vão como estão no contrato. Erros sobem para o card. */
  const saveCustomer = useCallback(
    async (values: RenewalPersonalFields) => {
      const quoteId = quote?.contract_details?.quote_id;
      if (!contract || quoteId == null) throw new Error('Cotação indisponível');
      await updateRenewalCustomer(
        quoteId,
        { ...values, document: contract.insured.document, birthdate: contract.insured.birthdate ?? '' },
        orgId
      );
      setContract((current) =>
        current
          ? {
              ...current,
              insured: {
                ...current.insured,
                name: values.name.trim(),
                email: values.email.trim(),
                mobile_phone: values.mobile_phone.replace(/\D/g, '')
              }
            }
          : current
      );
    },
    [contract, orgId, quote]
  );

  /** `POST Contracts/{hash}/insured_items/address`; o endereço salvo passa a valer na cotação em memória. */
  const saveAddress = useCallback(
    async (values: RenewalAddressFields) => {
      await updateRenewalAddress(contractHash, values, orgId);
      setQuote((current) =>
        current
          ? {
              ...current,
              address: { ...current.address, ...values, zipcode: values.zipcode.replace(/\D/g, '') }
            }
          : current
      );
    },
    [contractHash, orgId]
  );

  /**
   * Marca/desmarca um adicional e recalcula a cotação (`PUT /Contracts/quotes/{quoteId}`).
   * A seleção muda na hora; se o recálculo falhar, seleção e totais voltam ao anterior com aviso.
   */
  const toggleUpsell = useCallback(
    async (productId: number) => {
      const quoteId = quote?.contract_details?.quote_id;
      if (quoteId == null || !financials || recalculating) return;
      const previousIds = selectedIds;
      const previousFinancials = financials;
      const nextIds = previousIds.includes(productId)
        ? previousIds.filter((id) => id !== productId)
        : [...previousIds, productId];
      setSelectedIds(nextIds);
      setRecalculating(true);
      try {
        const totals = await updateRenewalQuoteProducts(quoteId, nextIds, orgId);
        setFinancials({
          ...previousFinancials,
          total: Number(totals.total),
          financial_charges: Number(totals.financial_charges),
          subtotal: Number(totals.subtotal),
          installments: Number(totals.installments ?? previousFinancials.installments),
          flat_rate:
            totals.flat_rate != null && Number(totals.flat_rate) > 0
              ? Number(totals.flat_rate)
              : previousFinancials.flat_rate
        });
      } catch (error: unknown) {
        setSelectedIds(previousIds);
        setFinancials(previousFinancials);
        reportError('Erro ao recalcular cotação da renovação 2026', error, {
          context: 'renewal_update_quote',
          flow: 'renewal',
          contractHash,
          quoteId,
          products: nextIds
        });
        storeState.addToast({
          title: 'Não foi possível atualizar o valor',
          message: 'Mantivemos as coberturas anteriores. Tente novamente.',
          type: 'error'
        });
      } finally {
        setRecalculating(false);
      }
    },
    [contractHash, financials, orgId, quote, recalculating, selectedIds]
  );

  /** Cartões do seletor: os adicionados nesta sessão no topo, depois os salvos do cliente. */
  const paymentCards = useMemo<JourneySavedCard[]>(
    () => [
      ...addedCards.map(({ id, brand, lastFour }) => ({ id, brand, lastFour })),
      ...cards.map((card) => ({ id: card.id, brand: toCardBrand(card.brand), lastFour: card.lastFourDigits }))
    ],
    [addedCards, cards]
  );

  /** Cartão tokenizado entra no topo, já selecionado; o formulário fecha porque a seleção sai de «novo». */
  const addCard = useCallback((card: JourneyAddedCard) => {
    setAddedCards((current) => [card, ...current.filter((existing) => existing.id !== card.id)]);
    setPaymentSelection(card.id);
  }, []);

  /** Dados do contrato novo que decidem quais fotos pedir (os mesmos campos do passo 2 da contratação). */
  const loadPhotosContext = useCallback(
    async (newContractId: string) => {
      setPhotosLoadState('loading');
      try {
        setPhotosContext(await getRenewedContract(newContractId));
        setPhotosLoadState('ready');
      } catch (error: unknown) {
        reportError('Erro ao carregar contrato renovado para as fotos (renovação 2026)', error, {
          context: 'renewal_photos_load_contract',
          flow: 'renewal',
          contractHash,
          renewedContractId: newContractId
        });
        setPhotosLoadState('error');
      }
    },
    [contractHash]
  );

  const photoFields = useMemo(
    () =>
      photosContext
        ? getContractUploadFields({
            hasInvoice: photosContext.hasInvoice,
            isNew: photosContext.isNew,
            productId: photosContext.productIds,
            equipmentTypeId: photosContext.equipmentTypeId
          })
        : [],
    [photosContext]
  );

  const changePhoto = useCallback((key: TypeFile, file: IFile) => {
    setPhotoFiles((current) => ({ ...current, [key]: file }));
    setPhotoErrors((current) => ({ ...current, [key]: false }));
  }, []);

  /** Valida os campos obrigatórios, envia as fotos do contrato novo e só então mostra a renovação concluída. */
  const submitPhotos = useCallback(async () => {
    if (!renewedContractId || sendingPhotosRef.current) return;
    const errors = await validateContractFiles(photoFields, photoFiles);
    if (Object.keys(errors).length > 0) {
      setPhotoErrors(errors);
      return;
    }
    sendingPhotosRef.current = true;
    setSendingPhotos(true);
    try {
      const payload: Partial<Record<TypeFile, IFile>> = {};
      photoFields.forEach((field) => {
        const file = photoFiles[field.key];
        if (file) payload[field.key] = file;
      });
      await sendRenewalPhotos(renewedContractId, payload, orgId);
      setPhotoFiles({});
      setOutcome('success');
      setStep('outcome');
    } catch (error: unknown) {
      reportError('Erro ao enviar fotos da renovação 2026', error, {
        context: 'renewal_photos_upload',
        flow: 'renewal',
        contractHash,
        renewedContractId,
        filesCount: Object.keys(photoFiles).length
      });
      storeState.addToast({
        title: 'Não foi possível enviar as fotos',
        message: 'Seu pagamento já foi aprovado. Tente enviar as fotos novamente.',
        type: 'error'
      });
    } finally {
      sendingPhotosRef.current = false;
      setSendingPhotos(false);
    }
  }, [contractHash, orgId, photoFields, photoFiles, renewedContractId]);

  /**
   * «Pagar e finalizar» / «Tentar novamente»: `PUT Contracts/quotes/{quoteId}/renew` com o cartão escolhido.
   * O ref impede envio duplo. Token de cartão novo vencido pede o cartão de novo, sem chamar a API.
   */
  const pay = useCallback(async () => {
    const quoteId = quote?.contract_details?.quote_id;
    if (payingRef.current || quoteId == null || !reviewData) return;
    const added = addedCards.find((card) => card.id === paymentSelection);
    if (!paymentSelection || paymentSelection === NEW_CARD_OPTION) return;
    payingRef.current = true;
    setPaying(true);
    setFailureMessage(null);
    setStep('processing');
    let stage: 'tokenize' | 'renew' = 'tokenize';
    try {
      // O token do Pagar.me dura 60 s e é de uso único: cartão novo ganha um token novo a cada tentativa.
      const newCardToken = added ? (await tokenizeCardWithPagarme(publicKey, added.cardInput)).token : undefined;
      const payload = buildRenewalPayload({
        selectedId: paymentSelection,
        addedCards,
        contractAddress: reviewData.address,
        organizationId: orgId,
        newCardToken
      });
      if (!payload) throw new Error('Pagamento sem cartão');
      stage = 'renew';
      const result = await renewContract(quoteId, payload);
      // Dados do cartão novo saem da memória assim que o pagamento é concluído.
      setAddedCards([]);
      if (getRenewalOutcome(result) === 'outdated') {
        // Fora do prazo: a renovação só conclui depois das fotos do equipamento do contrato novo.
        const newContractId = String(result.contract_id);
        setRenewedContractId(newContractId);
        setStep('photos');
        void loadPhotosContext(newContractId);
        return;
      }
      setOutcome('success');
      setStep('outcome');
    } catch (error: unknown) {
      const response = (
        error as { response?: { status?: number; data?: { notifications?: Array<{ message?: string }> } } }
      ).response;
      const status = response?.status ?? 0;
      // A API responde erro de negócio com 400 + `notifications` (não 422): qualquer 4xx com mensagem vale.
      const apiMessage = status >= 400 && status < 500 ? response?.data?.notifications?.[0]?.message ?? null : null;
      setFailureMessage(error instanceof CardTokenizationError ? error.message : apiMessage);
      reportError('Erro ao pagar a renovação 2026', error, {
        context: stage === 'tokenize' ? 'renewal_tokenize' : 'renewal_renew',
        flow: 'renewal',
        contractHash,
        quoteId,
        isNewCard: Boolean(added),
        status: response?.status
      });
      setOutcome('failure');
      setStep('outcome');
    } finally {
      payingRef.current = false;
      setPaying(false);
    }
  }, [addedCards, contractHash, loadPhotosContext, orgId, paymentSelection, publicKey, quote, reviewData]);

  const useAnotherCard = useCallback(() => {
    setOutcome(null);
    setFailureMessage(null);
    setStep('payment');
  }, []);

  const offerData = useMemo(() => {
    if (!quote || !financials) return null;
    return {
      coverageItems: getRenewalCoverageItems(quote),
      upsellItems: getRenewalUpsellItems(quote),
      selectedIds,
      summaryRows: buildRenewalSummaryRows(quote, selectedIds, financials),
      totalMonthly: financials.total,
      serviceCharge: financials.financial_charges,
      recalculating
    };
  }, [financials, quote, recalculating, selectedIds]);

  return {
    step,
    loadState,
    contract,
    channel,
    alternateChannel,
    sendingCode,
    resendingCode,
    code,
    codeStatus,
    quote,
    cards,
    quoteState,
    reviewData,
    offerData,
    paymentCards,
    addedCards,
    paymentSelection,
    paying,
    publicKey,
    publicKeyLoading,
    outcome,
    failureMessage,
    organizationId: orgId,
    photos: {
      loadState: photosLoadState,
      fields: photoFields,
      files: photoFiles,
      errors: photoErrors,
      sending: sendingPhotos
    },
    actions: {
      goTo: setStep,
      goBack,
      setChannel,
      sendCode,
      setCode,
      verifyCode,
      resendCode,
      switchChannel,
      changeContact,
      reloadQuote: loadQuoteAndCards,
      saveCustomer,
      saveAddress,
      toggleUpsell,
      renew: () => setStep('payment'),
      selectPayment: setPaymentSelection,
      addCard,
      pay,
      useAnotherCard,
      changePhoto,
      submitPhotos,
      reloadPhotos: () => (renewedContractId ? loadPhotosContext(renewedContractId) : undefined)
    }
  };
}
